import { AlertTriangle, ArrowLeft, HelpCircle, Home, RefreshCw } from 'lucide-react';
import { Accordion } from '@/components/ui/Accordion';
import { Button } from '@/components/ui/Button';
import { FeedbackState } from '@/components/ui/FeedbackState';
import { type ErrorMessageConfig, getErrorMessageFromError, getErrorMessageFromStatus } from './ErrorMessages';
import { useTranslation } from 'react-i18next';

export interface ErrorRecoveryProps {
  error?: Error | string | null;
  statusCode?: number;
  customMessage?: ErrorMessageConfig;
  onRetry?: () => void;
  onGoBack?: () => void;
  onGoHome?: () => void;
  showDetails?: boolean;
  className?: string;
}

/**
 * Reusable error recovery component with standardized actions
 */
export function ErrorRecovery({
  error,
  statusCode,
  customMessage,
  onRetry,
  onGoBack,
  onGoHome,
  showDetails = false,
  className = '',
}: ErrorRecoveryProps) {
  const { t } = useTranslation();

  // Determine error message configuration
  let errorConfig: ErrorMessageConfig;

  if (customMessage) {
    errorConfig = customMessage;
  } else if (statusCode) {
    errorConfig = getErrorMessageFromStatus(statusCode);
  } else if (error) {
    errorConfig = getErrorMessageFromError(error);
  } else {
    errorConfig = {
      title: t('errors.unexpectedError'),
      message: t('errors.unexpectedErrorMessage'),
      action: t('errors.tryAgain'),
      recoverable: true,
    };
  }

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  const action = (
    <div className='flex w-full max-w-sm flex-col items-center gap-4'>
      {showDetails && error && import.meta.env.DEV && (
        <div className='w-full p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-left text-sm'>
          <h3 className='font-semibold mb-2 flex items-center gap-2'>
            <HelpCircle className='h-4 w-4' />
            {t('errors.technicalDetails')}
          </h3>
          <pre className='whitespace-pre-wrap text-xs text-rose-600 dark:text-rose-400 overflow-auto max-h-32'>
            {typeof error === 'string' ? error : error.toString()}
          </pre>
          {error instanceof Error && error.stack && (
            <Accordion
              className='mt-2 border-gray-200 dark:border-gray-700 bg-transparent'
              title={t('errors.stackTrace')}
              summaryClassName='text-muted-strong px-0 py-1'
              contentClassName='px-0 pb-0 pt-2 border-t-0'
            >
              <pre className='whitespace-pre-wrap text-xs mt-2 text-muted-strong overflow-auto max-h-24'>
                {error.stack}
              </pre>
            </Accordion>
          )}
        </div>
      )}

      <div className='flex flex-col sm:flex-row gap-3 justify-center w-full'>
        {errorConfig.recoverable && (
          <Button variant='primary' onClick={handleRetry} className='flex items-center justify-center gap-2'>
            <RefreshCw className='h-4 w-4' />
            {errorConfig.action || t('errors.tryAgain')}
          </Button>
        )}

        {onGoBack && (
          <Button variant='outline' onClick={handleGoBack} className='flex items-center justify-center gap-2'>
            <ArrowLeft className='h-4 w-4' />
            {t('errors.goBack')}
          </Button>
        )}

        <Button variant='ghost' onClick={handleGoHome} className='flex items-center justify-center gap-2'>
          <Home className='h-4 w-4' />
          {t('errors.goHome')}
        </Button>
      </div>

      {!errorConfig.recoverable && <p className='text-xs text-muted'>{t('errors.contactSupport')}</p>}
    </div>
  );

  return (
    <FeedbackState
      icon={AlertTriangle}
      title={errorConfig.title}
      description={errorConfig.message}
      action={action}
      tone='danger'
      headingLevel='h2'
      className={className}
    />
  );
}

/**
 * Compact error recovery for inline use
 */
export function InlineErrorRecovery({
  error,
  onRetry,
  className = '',
}: {
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const errorConfig = error ? getErrorMessageFromError(error) : null;

  if (!errorConfig) return null;

  return (
    <div className={`flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg ${className}`}>
      <AlertTriangle className='h-5 w-5 text-red-500 shrink-0' />
      <div className='flex-1 text-sm'>
        <p className='font-medium text-red-800'>{errorConfig.title}</p>
        <p className='text-red-600'>{errorConfig.message}</p>
      </div>
      {errorConfig.recoverable && onRetry && (
        <Button
          variant='outline'
          size='sm'
          onClick={onRetry}
          className='shrink-0 border-red-300 text-red-700 hover:bg-red-100'
        >
          <RefreshCw className='h-3 w-3 mr-1' />
          {t('errors.tryAgain')}
        </Button>
      )}
    </div>
  );
}
